import * as R from 'ramda';
import { TS } from '../config/translation-keys.js';
import { t9n } from './translate.js';

// General utility functions ----------------------------------------------- //

/* The code below is defining a function called `exists` using the Ramda library. The function takes a
single argument and returns a boolean value. It checks if the argument is not null or undefined and
not an empty value (empty string, array, or object). The `R.both` function is used to combine two
predicates (functions that return a boolean value) using a logical AND operator. The `R.complement`
function is used to negate the result of the `R.isNil` and `R.isEmpty` functions, respectively. */
export const exists = R.both(R.complement(R.isNil), R.complement(R.isEmpty));

export const empty_fn = () => {};

export const log_fn = d => {
  console.log(d);
};

/**
 * The function generates a random integer between 0 and the maximum safe integer value in JavaScript.
 * @returns The `randint` function returns a random integer between 0 (inclusive) and the maximum safe
 * integer value (exclusive) using the `Math.random()` method and the `Math.floor()` method.
 */
export const randint = function() {
  return Math.floor(Math.random() * Number.MAX_SAFE_INTEGER);
};

/**
 * The function generates a unique identifier by combining the current timestamp and a random string.
 * @returns The `uid` function returns a unique identifier string that consists of the current
 * timestamp in base 36 and a random string of characters generated using the `Math.random()` method.
 */
export const uid = () => {
  return (
    Date.now().toString(36) +
    Math.random()
      .toString(36)
      .substr(2)
  );
};

/**
 * The function takes a string, removes spaces and parentheses, converts it to lowercase, and returns
 * either the entire string or the first five characters depending on the length of the original
 * string.
 * @returns The function `get_opt_id` returns a string that is either the trimmed and lowercased input
 * string `str` with all parentheses and plus signs removed, or the first five characters of that
 * string if its length is greater than or equal to 5.
 */
export const get_opt_id = str => {
  let val = str
    .trim()
    .replace(/[( )+]/g, '')
    .toLowerCase();
  return str.length < 5 ? val : val.substring(0, 5);
};

/**
 * The function checks if the URL contains a "debug=true" parameter and returns a boolean value.
 * @returns The function `is_debug` returns a boolean value indicating whether the URL query parameter
 * `debug=true` is present in the current window's location. If it is present, the function returns
 * `true`, otherwise it returns `false`.
 */
export const is_debug = () => {
  let debug_url = !!window && !!window.location.search.match('debug=true');
  return debug_url;
};

// Array methods ----------------------------------------------------------- //

/* The code below is adding a new method called `first` to the `Array` prototype in JavaScript. This
method returns the first element of the array if it exists, otherwise it returns null. */
if (!Array.prototype.first) {
  Array.prototype.first = function first(arr) {
    if (arr.length > 0) {
      return arr[0];
    }

    return null;
  };
}

/* The below code is defining a new method called `rest` on the `Array` prototype if it does not
already exist. This method returns a new array containing all the elements of the original array
except for the first element. If the original array is empty, an empty array is returned. */
if (!Array.prototype.rest) {
  Array.prototype.rest = function rest(arr) {
    if (arr.length > 0) {
      return arr.slice(1);
    }

    return [];
  };
}

/* The above code is adding a new method called "last" to the prototype of the Array object in
JavaScript. This method returns the last element of an array if the array is not empty, otherwise it
returns null. */
if (!Array.prototype.last) {
  Array.prototype.last = function last(arr) {
    if (arr.length > 0) {
      return arr[arr.length - 1];
    }

    return null;
  };
}

// map over a sequence in pairs of items
/**
 * The function `map_adjacent` takes a mapping function and an array, and returns a new array where
 * each element is the result of applying the mapping function to the previous and current elements of
 * the input array.
 * @param mapping - `mapping` is a function that takes two arguments and returns a value. It is used to
 * map adjacent elements in an array.
 */
export const map_adjacent = (mapping, [head, ...tail]) => {
  tail.reduceRight(
    (recur, item) => prev => [mapping(prev, item), ...recur(item)],
    _ => []
  )(head);
};

/**
 * The function filters out empty elements from a given list.
 * @param list - The parameter `list` is an array that contains elements to be filtered. The function
 * `filter_empty` takes this array as input and returns a new array that contains only the elements
 * that are not empty. The `filter` method is used to iterate over each element of the array and apply
 * the callback
 * @returns The `filter_empty` function is returning a new array that contains only the elements from
 * the input `list` that pass the test implemented by the provided function. In this case, the test
 * checks if each element `i` exists (i.e., is not null or undefined).
 */
export const filter_empty = function(list) {
  return list.filter(function(i) {
    return exists(i);
  });
};

// String methods ---------------------------------------------------------- //

/* The below code is adding a new method to the String prototype called `format`. This method takes any
number of arguments and replaces placeholders in the string with the corresponding argument. The
placeholders are in the form of `{n}` where `n` is the index of the argument to be used. The method
uses a regular expression to find all placeholders in the string and replaces them with the
corresponding argument. If an argument is not provided for a placeholder, the placeholder is left
unchanged. */
if (!String.prototype.format) {
  String.prototype.format = function() {
    let args = arguments;
    return this.replace(/{(\d+)}/g, function(match, number) {
      return typeof args[number] != 'undefined' ? args[number] : match;
    });
  };
}

// String helper functions ------------------------------------------------- //

/**
 * The function `capitalize_word` takes a word as input and returns the same word with the first letter
 * capitalized.
 * @returns The function `capitalize_word` takes a `word` parameter and returns the same word with the
 * first letter capitalized.
 */
export const capitalize_word = word => {
  return word.charAt(0).toUpperCase() + word.slice(1);
};

/**
 * The function `compareStrings` compares two strings in a case-insensitive manner and returns -1, 1,
 * or 0 depending on whether the first string is less than, greater than, or equal to the second
 * string.
 * @param a - The first string to be compared.
 * @param b - The parameter `b` is a string that will be compared to another string `a` in a
 * case-insensitive manner using the `compareStrings` function.
 * @returns The function `compareStrings` is returning -1 if the string `a` is less than the string
 * `b`, 1 if the string `a` is greater than the string `b`, and 0 if the strings are equal.
 */
export const compareStrings = (a, b) => {
  a = a.toLowerCase();
  b = b.toLowerCase();
  return a < b ? -1 : a > b ? 1 : 0;
};

/**
 * The function truncates a given text to a specified length and adds ellipsis if necessary.
 * @param text - The input text that needs to be truncated.
 * @param str_length - The maximum length of each line of text after truncation. If this parameter is
 * not provided, the default value of 40 is used.
 * @returns The function `truncate` returns a truncated version of the input `text` string, with a
 * maximum length of `str_length` characters. If the input string contains a newline character (`\n`),
 * the function will split the string into two parts and truncate each part separately. The truncated
 * string is returned as the output.
 */
export const truncate = function(text, str_length) {
  let r_text = text.split('\n'),
    str1 = r_text[0],
    str2 = r_text[1];

  if (str_length === undefined) {
    str_length = 40;
  }

  if (str1.length <= str_length) {
    r_text = str1;
    if (str2) {
      r_text += '\n';
      if (str2.length <= str_length) {
        r_text += str2;
      } else {
        r_text += str2.substr(0, str_length - 2) + '...';
      }
    }
  } else {
    r_text =
      str1.substr(0, str_length) +
      '\n' +
      str1.substr(str_length, str_length - 2) +
      '...';
  }

  return r_text;
};

// Binaries ---------------------------------------------------------------- //

// This is a simple trick to implement Blob.arrayBuffer (https://developer.mozilla.org/en-US/docs/Web/API/Blob/arrayBuffer) using FileReader
export const polyfill_array_buffer = () => {
  function myArrayBuffer() {
    // this: File or Blob
    return new Promise(resolve => {
      let fr = new FileReader();
      fr.onload = () => {
        resolve(fr.result);
      };
      fr.readAsArrayBuffer(this);
    });
  }

  if ('File' in self) {
    File.prototype.arrayBuffer = File.prototype.arrayBuffer || myArrayBuffer;
  }

  Blob.prototype.arrayBuffer = Blob.prototype.arrayBuffer || myArrayBuffer;
};

// For python to be able to process a file you need to base64 encode the result of this function
// Example:
//
// ```
// // document.getElementById('upload') is an <input type="file" id="upload" value="Browse...">
// const file = document.getElementById('upload').files[0];
// const binary = await file_to_binary(file)
// const b64_encoded_binary = btoa(binary);
// ```
/**
 * This function reads a file and returns its binary data as a promise.
 */
export const file_to_binary = async file => {
  const reader = new FileReader();
  reader.readAsBinaryString(file);
  reader.onload = () => Promise.resolve(reader.result);
  reader.onerror = error => Promise.reject(error);
};

/* The below code is defining a generator function called `range` that takes in three arguments:
`start`, `end`, and `step` (with a default value of 1). The function generates a sequence of numbers
from `start` to `end` (inclusive) with a step size of `step`. If the arguments are invalid and the
sequence would never finish, an error is thrown. The function uses recursion and the `yield` keyword
to generate the sequence of numbers. */
export const range = function*(start, end, step = 1) {
  if (
    (end - start) % step > 0 ||
    (end > start && step <= 0) ||
    (end > start && step >= 0)
  ) {
    throw new Error(
      `Wrong arguments: start=${start}, end=${end}, step=${step}; using them for range the processing would never finish.`
    );
  }
  yield start;
  if (start === end) return;
  yield* range(start + step, end, step);
};

// Date and time helpers --------------------------------------------------- //

/**
 * The function adds a leading zero to a time value if it is less than 10.
 * @returns If the input `time` is less than 10, the function returns a string with a leading zero and
 * the value of `time`. Otherwise, it simply returns the value of `time`.
 */
export const parse_time_value = time => {
  if (time < 10) return `0${time}`;
  return time;
};

/**
 * The function `parse_date` takes in two dates and two boolean values indicating whether the dates
 * have time values, and returns a formatted string representing the date range.
 * @param date1 - The start date of an event or time period.
 * @param date2 - The parameter `date2` is a date object representing the end date of a time period.
 * @param start_has_time - `start_has_time` is a boolean parameter that indicates whether the start
 * date/time has a time component or not. If it is `true`, the function will include the time component
 * in the parsed output. If it is `false`, the function will only include the date component.
 * @param end_has_time - The `end_has_time` parameter is a boolean value indicating whether the end
 * date/time has a time component or not. If it is `true`, the function will include the time component
 * in the parsed output. If it is `false`, the function will only include the date component.
 * @returns The function `parse_date` returns a formatted string representing the date range between
 * two given dates, with optional time values. The format of the string depends on the difference
 * between the start and end dates, and whether or not they have time values.
 */
export const parse_date = (date1, date2, start_has_time, end_has_time) => {
  let start_date = new Date(date1);
  let end_date = new Date(date2);
  // let offset = start_date.getTimezoneOffset() / 60;
  let offset = 0;
  let parsed_start = {
    day: parse_time_value(start_date.getDate()),
    month: parse_time_value(start_date.getMonth() + 1),
    year: start_date.getFullYear(),
    time: null,
  };
  if (!!start_has_time)
    parsed_start.time = `${parse_time_value(
      start_date.getHours() - offset
    )}:${parse_time_value(start_date.getMinutes())}`;
  let parsed_end = {
    day: parse_time_value(end_date.getDate()),
    month: parse_time_value(end_date.getMonth() + 1),
    year: end_date.getFullYear(),
    time: null,
  };
  if (!!end_has_time)
    parsed_end.time = `${parse_time_value(
      end_date.getHours() - offset
    )}:${parse_time_value(end_date.getMinutes())}`;

  let parsed_time = '';
  if (!!start_has_time) {
    parsed_time = `, ${parsed_start.time}`;
    if (!!end_has_time) {
      parsed_time = `, ${parsed_start.time}-${parsed_end.time}`;
    }
  }

  if (parsed_start.year !== parsed_end.year) {
    return `${parsed_start.day}.${parsed_start.month}.${parsed_start.year} - ${parsed_end.day}.${parsed_end.month}.${parsed_end.year}${parsed_time}`;
  } else {
    if (parsed_start.month !== parsed_end.month) {
      return `${parsed_start.day}.${parsed_start.month} - ${parsed_end.day}.${parsed_end.month}.${parsed_start.year}${parsed_time}`;
    } else {
      if (parsed_start.day !== parsed_end.day) {
        return `${parsed_start.day}. - ${parsed_end.day}.${parsed_start.month}.${parsed_start.year}${parsed_time}`;
      } else {
        return `${parsed_start.day}.${parsed_start.month}.${parsed_start.year}${parsed_time}`;
      }
    }
  }
  // return `${parsed_start} - ${parsed_end}`;
};

/**
 * The function formats a time value into a string with hours and minutes, with the option to use a
 * base format.
 * @param time - The time parameter is a number representing the time in hours and minutes, with the
 * minutes being represented as a decimal. For example, 2.5 would represent 2 hours and 30 minutes.
 * @param [base_format=false] - A boolean parameter that determines whether the function should return
 * the time in a base format (hh:mm) or in a more descriptive format (e.g. "2 hours 30 minutes"). If
 * set to true, the function will return the time in the base format. If set to false or not provided
 * @returns The function `format_float_time` returns a formatted time string based on the input `time`
 * parameter. If `base_format` is true, it returns the time in the format of "HH:MM". If `base_format`
 * is false, it returns the time in the format of "HH hours MM minutes". The minutes are rounded to the
 * nearest quarter hour (15, 30,
 */
export const format_float_time = (time, base_format = false) => {
  let val = String(time);
  let splitted = val.split('.');
  let hour = splitted[0];
  let minutes = Number(splitted[1]);
  if (!minutes) minutes = '00';
  if (minutes === 25) minutes = '15';
  if (minutes === 5) minutes = '30';
  if (minutes === 75) minutes = '45';
  if (base_format) {
    if (Number(hour) < 10) hour = `0${hour}`;
    return `${hour}:${minutes}`;
  }
  if (minutes === '00') {
    return `${hour} ${t9n(TS.NEW_ORDER_TIME_HOURS)}`;
  } else {
    return `${hour} ${t9n(TS.NEW_ORDER_TIME_HOURS)} ${minutes} ${t9n(
      TS.ORDER_DETAILS_MINUTES
    )}`;
  }
};

/**
 * The function formats a given date into a string with optional time and second parameters.
 * @param date - The date parameter is a string or a number representing a date and time. It is used to
 * create a new Date object.
 * @param [is_time=false] - is_time is a boolean parameter that determines whether or not to include
 * the time in the formatted date. If it is set to true, the time will be included in the format. If it
 * is set to false, only the date will be included.
 * @param [is_second=false] - The `is_second` parameter is a boolean flag that determines whether or
 * not to include the seconds in the formatted date and time string. If it is set to `true`, the
 * formatted string will include the seconds, otherwise it will only include the hours and minutes.
 * @returns The function `format_date` returns a formatted date string based on the input `date`. The
 * format of the string depends on the values of the `is_time` and `is_second` parameters. If `is_time`
 * is `false`, the function returns a string in the format "day.month.year". If `is_time` is `true`,
 * the function includes the time in the format "
 */
export const format_date = (date, is_time = false, is_second = false) => {
  let d = new Date(date);
  // let offset = 0;
  let offset = d.getTimezoneOffset() / 60;
  let plus_val = 0;
  if (is_safari()) plus_val = 0;

  let day = d.getDate() < 10 ? `${d.getDate()}` : d.getDate();
  let month = d.getMonth() + 1;
  let year = d.getFullYear();
  let hour =
    d.getHours() - offset + plus_val < 10
      ? `0${d.getHours() - offset + plus_val}`
      : d.getHours() - offset + plus_val;
  let minute = d.getMinutes() < 10 ? `0${d.getMinutes()}` : d.getMinutes();
  let second = d.getSeconds() < 10 ? `0${d.getSeconds()}` : d.getSeconds();
  if (!is_time) {
    return `${day}.${month}.${year}`;
  } else {
    if (!is_second) {
      return `${day}.${month}.${year}, ${hour}:${minute}`;
    } else {
      return `${day}.${month}.${year}, ${hour}:${minute}:${second}`;
    }
  }
};

/**
 * The function formats a given date and time into a string with hours and minutes, optionally
 * adjusting for timezone offset.
 * @param date - The date parameter is a string representing a date and time in the format "YYYY-MM-DD
 * HH:MM:SS".
 * @param [is_offset=true] - The `is_offset` parameter is a boolean value that determines whether or
 * not to adjust the time based on the user's timezone offset. If `is_offset` is `true`, the function
 * will subtract the user's timezone offset from the time to display the correct time in the user's
 * timezone. If
 * @returns The function `format_time` returns a formatted time string in the format of `HH:MM` (hours
 * and minutes) based on the input `date`. The `is_offset` parameter is optional and defaults to
 * `true`. If `is_offset` is `true`, the function takes into account the timezone offset of the user's
 * device.
 */
export const format_time = (date, is_offset = true) => {
  let d = new Date(date.replace(' ', 'T'));

  let offset = 0;
  if (is_offset) {
    offset = d.getTimezoneOffset() / 60;
  }
  let plus_val = 0;
  let hour =
    d.getHours() - offset + plus_val < 10
      ? `0${d.getHours() - offset + plus_val}`
      : d.getHours() - offset + plus_val;
  let minute = d.getMinutes() < 10 ? `0${d.getMinutes()}` : d.getMinutes();
  return `${hour}:${minute}`;
};

/**
 * The function formats multiple dates and times into a readable string format.
 * @param date1 - The first date to be formatted (required).
 * @param date2 - The second date to be formatted. It is an optional parameter, meaning it can be left
 * blank.
 * @param [is_time=false] - A boolean flag indicating whether to include time in the formatted output.
 * @param time1 - The start time of the event on date1.
 * @param time2 - `time2` is a string representing the end time of an event or activity. It is used in
 * conjunction with `date2` to format a range of dates and times. If `date2` is not provided, `time2`
 * is assumed to be the end time of `date1
 * @param [is_just_start_time=false] - The is_just_start_time parameter is a boolean flag that
 * determines whether to only display the start time or both start and end times in the formatted
 * output.
 * @param [inited_time=false] - The inited_time parameter is a boolean flag that indicates whether the
 * time values have already been initialized or not. If it is set to true, the function will use the
 * provided time values (time1 and time2) as is, without formatting them. If it is set to false, the
 * function
 * @param [is_offset=true] - The is_offset parameter is a boolean flag that determines whether to apply
 * a time zone offset to the formatted time values. If set to true, the function will adjust the time
 * values based on the user's time zone. If set to false, the function will return the time values as
 * they are without any
 * @returns The function `format_multiple_dates` returns a formatted string that represents one or
 * multiple dates and times, depending on the input parameters. The format of the string can be
 * customized using the optional parameters.
 */
export const format_multiple_dates = (
  date1,
  date2,
  is_time = false,
  time1,
  time2,
  is_just_start_time = false,
  inited_time = false,
  is_offset = true
) => {
  let d1 = format_date(date1);
  let d2;
  if (!!date2) {
    d2 = format_date(date2);
  }

  let t1, t2;
  if (!inited_time) {
    t1 = format_time(`${date1} ${time1}`, is_offset);
    if (!!date2) {
      t2 = format_time(`${date2} ${time2}`, is_offset);
    } else {
      t2 = format_time(`${date1} ${time2}`, is_offset);
    }
  } else {
    t1 = time1;
    t2 = time2;
  }

  if (!is_time) {
    if (d1 === d2) {
      return d1;
    } else {
      if (!!d2) {
        return `${d1} - ${d2}`;
      } else {
        return `${d1}`;
      }
    }
  } else {
    if (d1 === d2) {
      if (is_just_start_time) {
        return `${d1}, ${t1}`;
      } else {
        return `${d1}, ${t1} - ${t2}`;
      }
    } else {
      if (is_just_start_time) {
        if (!!d2) {
          return `${d1} - ${d2}, ${t1}`;
        } else {
          return `${d1}, ${t1}`;
        }
      } else {
        if (!!d2) {
          return `${d1} - ${d2}, ${t1} - ${t2}`;
        } else {
          if (!!t2) {
            return `${d1}, ${t1} - ${t2}`;
          } else {
            return `${d1}, ${t1}`;
          }
        }
      }
    }
  }
};

/**
 * The function reparse_date takes a date string and returns a formatted date string in the user's
 * local time zone.
 * @returns The function `reparse_date` takes a string `date_str` as input, parses it into a `Date`
 * object using `Date.parse()`, and then returns a string representation of the date in the user's
 * local time zone using `toLocaleString()`. Therefore, the function returns a string representation of
 * the parsed date in the user's local time zone.
 */
export const reparse_date = date_str => {
  return new Date(Date.parse(date_str)).toLocaleString();
};

// Object helpers ---------------------------------------------------------- //

/**
 * This is a JavaScript function that sorts an array of objects based on a specified attribute, with an
 * option to reverse the order.
 * @param obj_array - an array of objects that you want to sort
 * @param attribute - The attribute parameter is a string that represents the key of the object
 * property that will be used to sort the array of objects. For example, if the objects in the array
 * have a "name" property, the attribute parameter would be "name".
 * @param is_reverse - The is_reverse parameter is a boolean value that determines whether the sorting
 * order should be reversed or not. If it is set to true, the array will be sorted in descending order,
 * otherwise it will be sorted in ascending order. If this parameter is not provided, the default value
 * is false, which means
 * @returns The function `sort_objects` is returning a sorted array of objects based on the specified
 * attribute and sorting order.
 */
export const sort_objects = function(obj_array, attribute, is_reverse) {
  const reverse = exists(is_reverse) ? is_reverse : false;

  return obj_array.sort(function(i1, i2) {
    if (i1[attribute] > i2[attribute]) {
      return reverse ? -1 : 1;
    } else if (i1[attribute] < i2[attribute]) {
      return reverse ? 1 : -1;
    } else {
      return 0;
    }
  });
};

/**
 * The function retrieves the value of an object property using a string path.
 * @param path_str - The path string is a string that represents the path to a specific property in an
 * object. It uses dot notation to indicate nested properties. For example, "person.name.first" would
 * refer to the "first" property of the "name" object, which is a property of the "person" object
 * @param obj - The `obj` parameter is an object that we want to extract a value from using a given
 * path.
 * @returns The function `get_object_value_by_path` returns the value of the property in the object
 * specified by the path string. If the path string is empty or undefined, it returns the entire
 * object. If the path does not exist in the object, it returns undefined.
 */
export const get_object_value_by_path = (path_str, obj) => {
  let value;

  try {
    if (!!path_str) {
      let names = path_str.split('.');
      value = names.reduce((acc = obj, n) => {
        if (n.trim().length > 0 && !isNaN(n)) n = +n;
        return acc[n];
      }, obj);
    } else {
      value = obj;
    }
  } catch (e) {
    console.log(e);
    value = undefined;
  }

  return value;
};

// Browser helpers --------------------------------------------------------- //

/**
 * The function checks if the user's browser is Safari.
 * @returns The function `is_safari` returns a boolean value indicating whether the user's browser is
 * Safari or not.
 */
export const is_safari = () => {
  let safari =
    navigator.vendor &&
    navigator.vendor.indexOf('Apple') > -1 &&
    navigator.userAgent &&
    navigator.userAgent.indexOf('CriOS') == -1 &&
    navigator.userAgent.indexOf('FxiOS') == -1;
  return safari;
};

/**
 * This function decodes and parses the query string from the URL into an object containing key-value
 * pairs.
 * @returns This code exports a function named `urlparams` that returns an object containing key-value
 * pairs parsed from the query string of the current URL. The function first decodes the query string
 * using `decodeURIComponent`, then splits it into an array of key-value pairs using `split('&')`, and
 * finally reduces the array into an object using `reduce()`.
 */
export const urlparams = () => {
  return decodeURIComponent(window.location.search)
    .slice(1) // getting the query string without the prepending '?'
    .split('&') // splitting at '&' will give us the key=value pairs
    .map(item => {
      return item.split('='); // going over on them to create an array of tuples
    })
    .reduce((obj, item) => {
      obj[item[0]] = item[1]; // and reducing them into an object at the end
      return obj;
    }, {});
};

/**
 * The function checks if an element is visible on the page.
 * @returns The function `is_visible` takes an element as an argument and returns a boolean value. It
 * returns `true` if the element is visible on the page (i.e. has a non-zero width and height, or has
 * at least one non-empty client rectangle), and `false` otherwise. The double negation (`!!`) is used
 * to convert the truthy/falsy value returned by the expression
 */
export const is_visible = element => {
  return !!(
    element.offsetWidth ||
    element.offsetHeight ||
    element.getClientRects().length
  );
};

/**
 * The function injects a stylesheet into the HTML document by creating a link element and appending it
 * to the head of the document.
 */
export const inject_style_sheet = path => {
  let link = document.createElement('link');
  link.rel = 'stylesheet';
  link.type = 'text/css';
  link.href = path;
  document.head.append(link);
};

/**
 * The function injects a set of CSS rules into the head of an HTML document.
 */
export const inject_style_rules = rules => {
  let style = document.createElement('style');
  style.textContent = rules;
  document.head.append(style);
};

/**
 * This function removes a specified style sheet from the HTML document.
 */
export const remove_style_sheet = path => {
  const link = document.querySelector(`link[href="${path}"]`);

  if (link) {
    link.remove();
  }
};

/**
 * This function opens a new window with a specified title and URL when a link is clicked.
 */
export const open_window = event => {
  event.preventDefault();

  const source = event.target;
  const href = source.href;
  const new_title = source.dataset.title;
  const new_window = window.open(href, '_blank');

  setTimeout(() => {
    new_window.document.title = new_title;
  }, 500);

  event.returnValue = false;
};

/**
 * This function creates a downloadable link for a given data and file name, and then removes it from
 * the document body after it is clicked.
 * @param data - The data parameter is the content of the file that needs to be downloaded. It could be
 * a string, a blob, or a file object.
 * @param file_name - The name that the downloaded file will have.
 */
export const download_file = (data, file_name) => {
  let link = document.createElement('a');
  link.download = file_name;
  link.dataset.title = file_name;
  link.href = data;
  link.target = '_blank';
  document.body.appendChild(link);

  // link.addEventListener("click", open_window);
  link.click();
  // link.removeEventListener("click", open_window);

  document.body.removeChild(link);
};

/**
 * The function logs an event to the console.
 */
export const log_event = e => {
  console.log(e);
};

/**
 * This function creates a custom event called "pageready" with certain properties.
 * @returns A function that creates and returns a new CustomEvent object with the name 'pageready' and
 * the specified options for bubbles, cancelable, and composed.
 */
export const create_pageready_event = () => {
  return new CustomEvent('pageready', {
    bubbles: true,
    cancelable: false,
    composed: true,
  });
};

/**
 * The function attempts to fix an autofill issue by dispatching a custom event on the document body.
 */
export const fix_for_autofill = () => {
  // autofill fix - not working, only from console...
  setTimeout(() => {
    document.body.addEventListener('pageready', log_event);
    document.body.dispatchEvent(create_pageready_event());
    document.body.removeEventListener('pageready', log_event);
  }, 0);
};

/**
 * The function returns the current timestamp as a number.
 * @returns The `timestamp` function returns the current timestamp as the number of milliseconds since
 * January 1, 1970, 00:00:00 UTC, as returned by the `valueOf()` method of a `Date` object.
 */
export const timestamp = () => {
  return new Date().valueOf();
};

// Network helpers --------------------------------------------------------- //

/**
 * This is a JavaScript function that implements the debounce technique to limit the frequency of
 * function calls.
 * @param func - The function that needs to be debounced.
 * @param wait - The `wait` parameter is the amount of time in milliseconds that the function should
 * wait before executing the `func` function. This is used in conjunction with the `setTimeout`
 * function to delay the execution of the `func` function.
 * @param immediate - A boolean value that determines whether the function should be called immediately
 * or after a delay. If set to true, the function will be called immediately and then wait for the
 * specified delay before allowing another call. If set to false, the function will wait for the
 * specified delay before being called for the first time
 * @returns A function that implements the debounce functionality.
 */
export const debounce = function(func, wait, immediate) {
  let timeout;

  return function() {
    let context = this;
    let args = arguments;

    let later = function() {
      timeout = null;

      if (!immediate) {
        func.apply(context, args);
      }
    };

    let call_now = immediate && !timeout;

    clearTimeout(timeout);

    timeout = setTimeout(later, wait);

    if (call_now) {
      func.apply(context, args);
    }
  };
};

/**
 * The "once" function returns a new function that can only be called once, and subsequent calls will
 * return null.
 * @param func - The `func` parameter is a function that will be executed only once when the returned
 * function is called for the first time.
 * @returns A function that wraps the original function passed as an argument and ensures that it can
 * only be called once. The returned function checks if it has been called before and if not, it sets a
 * flag to indicate that it has been called and then calls the original function with the provided
 * arguments. If the function has already been called, it returns null.
 */
export const once = function(func) {
  let is_called = false;

  return function() {
    let context = this;
    let args = arguments;

    if (!is_called) {
      is_called = true;
      return func.apply(context, args);
    }

    return null;
  };
};

/**
 * The function checks if a given subdomain is included in a list of default subdomains in the settings
 * object.
 * @param settings - The `settings` parameter is likely an object that contains various settings or
 * configurations for a system or application. It is being passed as an argument to the
 * `default_subdomain` function.
 * @param subdomain - The `subdomain` parameter is a string representing a subdomain that is being
 * checked against a list of default subdomains in the `settings` object.
 * @returns The function `default_subdomain` takes in two parameters: `settings` and `subdomain`. It
 * checks if the `subdomain` parameter is included in the `default_subdomains` array of the `settings`
 * object. If it is, the function returns `true`, otherwise it returns `false`.
 */
export const default_subdomain = (settings, subdomain) => {
  return settings.default_subdomains.includes(subdomain);
};

/**
 * This function extracts the subdomain from a given URL.
 * @returns The function `subdomain_from` takes a URL as input and returns the subdomain of that URL.
 * If the URL is `https://www.example.com`, the function will return `www`. If the URL is
 * `https://subdomain.example.com`, the function will return `subdomain`. If the URL is
 * `https://example.com`, the function will return an empty string.
 */
export const subdomain_from = url => {
  const sub = url.split('.')[0];
  return sub.includes('//') ? sub.split('//')[1] : sub;
};

/**
 * The function concatenates a subdomain and a main domain to form a full domain.
 * @param sub - The `sub` parameter is a string representing a subdomain.
 * @param main - The `main` parameter is a string representing the main domain name.
 * @returns The function `full_domain_for` takes two parameters `sub` and `main` and returns a string
 * that concatenates `sub` and `main` with a period in between. For example, if `sub` is "www" and
 * `main` is "example.com", the function would return "www.example.com".
 */
export const full_domain_for = (sub, main) => {
  return `${sub}.${main}`;
};

/**
 * This function returns a URL with a specified subdomain and base domain.
 * @param settings - The `settings` parameter is an object that contains various settings related to
 * the website or application, such as the default subdomain and base domain.
 * @param [subdomain=null] - The subdomain parameter is an optional parameter that can be passed to the
 * url_for function. If a subdomain is provided, it will be used in the generated URL. If no subdomain
 * is provided, the default subdomain specified in the settings object will be used.
 * @returns The function `url_for` is returning a string that represents a URL with the subdomain and
 * base domain specified in the `settings` object. If a `subdomain` argument is provided, it will be
 * used instead of the default subdomain specified in the `settings` object. The URL is constructed
 * using the `full_domain_for` function, which is not shown in the code snippet.
 */
export const url_for = (settings, subdomain = null) => {
  const sub = subdomain ? subdomain : settings.default_subdomain;
  return `https://${full_domain_for(sub, settings.base_domain)}`;
};

/**
 * This function redirects the user to a different subdomain if their current subdomain does not match
 * the one specified in the session settings.
 * @param session - The `session` parameter is likely an object that contains information about the
 * current user session, such as their authentication status, user ID, and any other relevant data.
 * @param settings - It is a variable that contains settings related to the website or application. It
 * could include things like API keys, database connection information, or other configuration options.
 * Without seeing the full code, it's hard to say exactly what is included in the settings variable.
 */
export const handle_different_subdomain = (session, settings) => {
  if (session.value().merchant && session.value().merchant.subdomain) {
    let full_url = url_for(settings, session.value().merchant.subdomain);

    if (!window.location.href.includes(full_url)) {
      window.location.assign(full_url);
    }
  }
};

// Event helpers --------------------------------------------------------- //

/**
 * This function checks if a key press event is a character key press.
 * @returns The function `is_character_key_press` takes an event object `e` as input and returns a
 * boolean value.
 */
export const is_character_key_press = e => {
  if (typeof e.which == 'undefined') {
    return true;
  } else if (typeof e.which == 'number' && e.which > 0) {
    return !e.ctrlKey && !e.metaKey && !e.altKey && e.which != 8;
  }
  return false;
};
